import type { SupabaseClient } from "@supabase/supabase-js";
import { ScheduleConflictError, isScheduleConflict } from "@produce/shared";

// One-shot wrapper around the apply_assignment_time_patches RPC
// (migration 20260521120300). Extracted so the call signature is
// unit-testable and so we keep the (`as any`) cast for the RPC name
// in one place — generated types lag behind new migrations.
//
// Background: the producer editor's post-lineup-save sync used to
// fire N parallel `from("crew_assignments").update(patch).eq("id", ...)`
// calls. Each one was its own PostgREST transaction + WAL record +
// Realtime broadcast. Coalescing into a single RPC removes the
// per-row network and transaction overhead; the RPC's diff-guard
// drops no-op patches at the DB level.

export interface AssignmentTimePatch {
  id: string;
  filming_start: string;
  filming_end: string;
  // `artist_name` is intentionally optional in the input shape: the
  // RPC distinguishes "key absent" (preserve existing value) from
  // "key present, null" (clear). Pass undefined to preserve.
  artist_name?: string;
}

export interface ApplyAssignmentPatchesResult {
  updated: number;
  deactivated: number;
}

/**
 * With `expectedScheduleRevision` (the revision the editor's schedule check
 * passed, lib/saveFlow/scheduleRevision.ts) it writes through
 * apply_assignment_time_patches_at_revision: refused, with nothing written,
 * when a phone edit moved the revision since — thrown as
 * ScheduleConflictError. null writes unchecked, as before.
 */
export async function applyAssignmentTimePatches(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: SupabaseClient<any, any, any>,
  briefingId: string,
  patches: AssignmentTimePatch[],
  deactivateIds: string[] = [],
  expectedScheduleRevision: number | null = null,
): Promise<ApplyAssignmentPatchesResult> {
  // Empty payload short-circuit — no RPC roundtrip when there's
  // nothing to patch or deactivate. Mirrors the existing
  // `if (timeUpdates.length > 0)` guard in CreateEditBriefing so
  // we don't trade one wasted call for another.
  if (patches.length === 0 && deactivateIds.length === 0) {
    return { updated: 0, deactivated: 0 };
  }

  const args = {
    p_briefing_id: briefingId,
    p_patches: patches,
    p_deactivate_ids: deactivateIds.length > 0 ? deactivateIds : null,
  };
  const { data, error } =
    expectedScheduleRevision === null
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (client.rpc as any)("apply_assignment_time_patches", args)
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (client.rpc as any)("apply_assignment_time_patches_at_revision", {
          ...args,
          p_expected_schedule_revision: expectedScheduleRevision,
        });
  if (error) {
    if (isScheduleConflict(error)) throw new ScheduleConflictError(error.message);
    throw new Error(`apply_assignment_time_patches failed: ${error.message}`);
  }
  return {
    updated: (data?.updated as number) ?? 0,
    deactivated: (data?.deactivated as number) ?? 0,
  };
}
